/**
 * Shared multi-driver scenario contracts.
 *
 * This module declares and validates scenario definitions only. Selector
 * resolution, formula evaluation, narration, activation, and rendering remain
 * owned by later engine/playbook lanes.
 */

export const SCENARIO_CASE_IDS = ['conservative', 'base', 'stretch'] as const;

export const SCENARIO_DRIVER_UNITS = [
  'percent',
  'usd',
  'bps',
  'ratio',
  'count',
] as const;

export const SCENARIO_DRIVER_ROW_AGGREGATIONS = [
  'sum',
  'value_weighted_mean',
  'row_share',
] as const;

export type ScenarioCaseId = (typeof SCENARIO_CASE_IDS)[number];
export type ScenarioDriverUnit = (typeof SCENARIO_DRIVER_UNITS)[number];
export type ScenarioDriverRowAggregation =
  (typeof SCENARIO_DRIVER_ROW_AGGREGATIONS)[number];

/** A driver can bind only to governed tenant data or an explicitly labeled benchmark. */
export type ScenarioDriverBinding =
  | Readonly<{
      kind: 'governed_signal';
      stateKey: string;
    }>
  | Readonly<{
      kind: 'governed_rows';
      rowPath: string;
      aggregation: ScenarioDriverRowAggregation;
    }>
  | Readonly<{
      kind: 'labeled_benchmark';
      referencePackContextKind: string;
      metricId: string;
      derivation: string;
    }>;

export type ScenarioDriverSettingSelector =
  | Readonly<{ kind: 'observed_baseline' }>
  | Readonly<{
      kind: 'portfolio_stat';
      stat: 'minimum' | 'median' | 'maximum';
    }>
  | Readonly<{
      kind: 'benchmark_band_edge';
      edge: 'low' | 'high';
    }>
  | Readonly<{ kind: 'strategy_target' }>
  | Readonly<{
      kind: 'governed_fraction_of_baseline';
      factorPct: number;
    }>;

export type ScenarioDriverDomainBound = Readonly<{
  value: number;
  rationale: string;
  sourceRef: string;
}>;

export type ScenarioDriverDefinition = Readonly<{
  driverId: string;
  label: string;
  unit: ScenarioDriverUnit;
  binding: ScenarioDriverBinding;
  domain: Readonly<{
    min: ScenarioDriverDomainBound;
    max: ScenarioDriverDomainBound;
  }>;
  narrationTemplate: string;
}>;

/**
 * A metric registered here is an outcome of the named drivers and must never
 * be presented as another independent assumption.
 */
export type ScenarioDerivedMetricDeclaration = Readonly<{
  metricKey: string;
  derivedFromDriverIds: readonly string[];
  derivation: string;
}>;

/** Authoritative registry entry: deterministic metrics are display outcomes, never axes. */
export type ScenarioDerivedMetricRegistryEntry = Readonly<{
  metricKey: string;
  derivedFromDriverIds: readonly string[];
  derivation: string;
  renderAs: 'outcome';
}>;

export type ScenarioCasePoint = Readonly<{
  caseId: ScenarioCaseId;
  label: string;
  riskLevel: 'low' | 'medium' | 'high';
  driverSettings: Readonly<Record<string, ScenarioDriverSettingSelector>>;
  objective: string;
  recommendation: string;
  recommended: boolean;
}>;

export type MultiDriverScenarioPlaybook = Readonly<{
  playbookId: string;
  version: string;
  formulaId: string;
  formulaVersion: string;
  drivers: readonly ScenarioDriverDefinition[];
  derivedMetrics: readonly ScenarioDerivedMetricDeclaration[];
  cases: readonly ScenarioCasePoint[];
}>;

export type ScenarioDriverBindingClass =
  | 'binds_to_governed_data'
  | 'labeled_benchmark';

/** Stable metadata used by the design-time binding-distinctness gate. */
export type ScenarioDriverBindingDistinctnessMetadata = Readonly<{
  driverId: string;
  bindingClass: ScenarioDriverBindingClass;
  bindingIdentity: string;
}>;

export type ScenarioCollinearityReasonCode =
  | 'shared_binding_identity'
  | 'derived_metric_declared_as_driver';

export type ScenarioCollinearityViolation = Readonly<{
  reasonCode: ScenarioCollinearityReasonCode;
  driverIds: readonly string[];
  canonicalDriverId: string;
  bindingIdentity?: string;
  derivedMetricKey?: string;
  derivation?: string;
}>;

export type ScenarioCollinearityReport =
  | Readonly<{
      status: 'independent';
      violations: readonly [];
    }>
  | Readonly<{
      status: 'collinear';
      violations: readonly ScenarioCollinearityViolation[];
    }>;

/** Formula-owned explainability output for one resolved named posture. */
export type ScenarioDriverFormulaOutput = Readonly<{
  caseValueUsd: number;
  baselineValueUsd: number;
  baselineVarianceUsd: number;
  driverEcho: Readonly<Record<string, number>>;
  axisContributionsUsd: Readonly<Record<string, number>>;
  interactionResidualUsd: number;
}>;

export type ScenarioTargetBindingConstraint = Readonly<{
  driverId: string;
  limitValue: number;
  limitRationale: string;
  sourceRef: string;
  axisHeadroomUsd: number;
}>;

/** Formula result at the governed feasible-max driver corner. */
export type ScenarioFeasibleMaxCornerEvaluation = Readonly<{
  maxReachableUsd: number;
  bindingConstraints: readonly ScenarioTargetBindingConstraint[];
}>;

export type ScenarioReachableTargetVerdict = Readonly<{
  status: 'reachable';
  requestedTargetUsd: number;
  casePoint: ScenarioCasePoint;
}>;

export type ScenarioNonexecutableTargetVerdict = ScenarioFeasibleMaxCornerEvaluation
  & Readonly<{
    status: 'nonexecutable';
    reasonCode: 'target_exceeds_reachable_driver_space';
    requestedTargetUsd: number;
    narrative: string;
  }>;

export type ScenarioTargetVerdict =
  | ScenarioReachableTargetVerdict
  | ScenarioNonexecutableTargetVerdict;

export type MultiDriverScenarioPlaybookContractContext = Readonly<{
  /** Authoritative outcome registry, independent of declarations carried by the playbook. */
  derivedMetricRegistry: readonly ScenarioDerivedMetricRegistryEntry[];
  /** Every card metric that is deterministically computable from the driver vector. */
  derivedCardMetricKeys: readonly string[];
  /** C2-safe case-owned numeric binding key for each named posture. */
  caseNumericBindingKeys: Readonly<Record<ScenarioCaseId, string>>;
  /** Numeric binding keys owned by the governed aggregate envelope. */
  aggregateNumericBindingKeys: readonly string[];
}>;

export type ScenarioContractParseResult<T> =
  | Readonly<{ success: true; data: T }>
  | Readonly<{ success: false; error: Error }>;

export const MultiDriverScenarioPlaybookSchema = {
  parse(input: unknown): MultiDriverScenarioPlaybook {
    return parseMultiDriverScenarioPlaybook(input);
  },
  safeParse(input: unknown): ScenarioContractParseResult<MultiDriverScenarioPlaybook> {
    return safeParse(() => parseMultiDriverScenarioPlaybook(input));
  },
};

export const ScenarioDriverFormulaOutputSchema = {
  parse(
    input: unknown,
    expectedDriverIds?: readonly string[],
  ): ScenarioDriverFormulaOutput {
    return parseScenarioDriverFormulaOutput(input, expectedDriverIds);
  },
  safeParse(
    input: unknown,
    expectedDriverIds?: readonly string[],
  ): ScenarioContractParseResult<ScenarioDriverFormulaOutput> {
    return safeParse(() => parseScenarioDriverFormulaOutput(input, expectedDriverIds));
  },
};

export const ScenarioDerivedMetricRegistrySchema = {
  parse(
    input: unknown,
    declaredDriverIds: readonly string[],
  ): readonly ScenarioDerivedMetricRegistryEntry[] {
    return parseScenarioDerivedMetricRegistry(input, declaredDriverIds);
  },
  safeParse(
    input: unknown,
    declaredDriverIds: readonly string[],
  ): ScenarioContractParseResult<readonly ScenarioDerivedMetricRegistryEntry[]> {
    return safeParse(() =>
      parseScenarioDerivedMetricRegistry(input, declaredDriverIds),
    );
  },
};

export const ScenarioFeasibleMaxCornerEvaluationSchema = {
  parse(
    input: unknown,
    drivers: readonly ScenarioDriverDefinition[],
  ): ScenarioFeasibleMaxCornerEvaluation {
    return parseScenarioFeasibleMaxCornerEvaluation(input, drivers);
  },
  safeParse(
    input: unknown,
    drivers: readonly ScenarioDriverDefinition[],
  ): ScenarioContractParseResult<ScenarioFeasibleMaxCornerEvaluation> {
    return safeParse(() => parseScenarioFeasibleMaxCornerEvaluation(input, drivers));
  },
};

export const ScenarioTargetVerdictSchema = {
  parse(
    input: unknown,
    drivers: readonly ScenarioDriverDefinition[],
  ): ScenarioTargetVerdict {
    return parseScenarioTargetVerdict(input, drivers);
  },
  safeParse(
    input: unknown,
    drivers: readonly ScenarioDriverDefinition[],
  ): ScenarioContractParseResult<ScenarioTargetVerdict> {
    return safeParse(() => parseScenarioTargetVerdict(input, drivers));
  },
};

/**
 * Projects a binding into the two honest display classes and a stable source
 * identity. Aggregation strategy and benchmark derivation do not make a
 * shared source distinct: stateKey, rowPath, or reference-pack metric owns the
 * binding identity.
 */
export function getScenarioDriverBindingDistinctnessMetadata(
  driver: ScenarioDriverDefinition,
): ScenarioDriverBindingDistinctnessMetadata {
  const { binding } = driver;
  switch (binding.kind) {
    case 'governed_signal':
      return {
        driverId: driver.driverId,
        bindingClass: 'binds_to_governed_data',
        bindingIdentity: JSON.stringify(['governed_signal', binding.stateKey]),
      };
    case 'governed_rows':
      return {
        driverId: driver.driverId,
        bindingClass: 'binds_to_governed_data',
        bindingIdentity: JSON.stringify(['governed_rows', binding.rowPath]),
      };
    case 'labeled_benchmark':
      return {
        driverId: driver.driverId,
        bindingClass: 'labeled_benchmark',
        bindingIdentity: JSON.stringify([
          'labeled_benchmark',
          binding.referencePackContextKind,
          binding.metricId,
        ]),
      };
  }
}

function getScenarioDriverBindingMetricKey(
  binding: ScenarioDriverBinding,
): string {
  switch (binding.kind) {
    case 'governed_signal':
      return binding.stateKey;
    case 'governed_rows':
      return binding.rowPath;
    case 'labeled_benchmark':
      return binding.metricId;
  }
}

/**
 * Detects definitional collinearity only. Co-movement across the three named
 * posture profiles is policy correlation and is intentionally not inspected.
 */
export function detectScenarioDriverCollinearity(
  drivers: readonly ScenarioDriverDefinition[],
  derivedMetrics: readonly ScenarioDerivedMetricDeclaration[],
): ScenarioCollinearityReport {
  const violations: ScenarioCollinearityViolation[] = [];
  const bindings = new Map<string, ScenarioDriverBindingDistinctnessMetadata[]>();

  for (const driver of drivers) {
    const metadata = getScenarioDriverBindingDistinctnessMetadata(driver);
    const group = bindings.get(metadata.bindingIdentity) ?? [];
    group.push(metadata);
    bindings.set(metadata.bindingIdentity, group);
  }

  for (const [bindingIdentity, group] of bindings) {
    if (group.length < 2) continue;
    violations.push({
      reasonCode: 'shared_binding_identity',
      driverIds: group.map((metadata) => metadata.driverId),
      canonicalDriverId: group[0].driverId,
      bindingIdentity,
    });
  }

  const declaredDriverIds = new Set(drivers.map((driver) => driver.driverId));
  for (const derivedMetric of derivedMetrics) {
    const misdeclaredDriver = drivers.find(
      (driver) =>
        driver.driverId === derivedMetric.metricKey
        || getScenarioDriverBindingMetricKey(driver.binding) === derivedMetric.metricKey,
    );
    if (misdeclaredDriver === undefined) continue;
    const sourceDriverIds = derivedMetric.derivedFromDriverIds.filter(
      (driverId) =>
        driverId !== misdeclaredDriver.driverId && declaredDriverIds.has(driverId),
    );
    const collinearDriverIds =
      sourceDriverIds.length > 0
        ? [...sourceDriverIds, misdeclaredDriver.driverId]
        : [misdeclaredDriver.driverId];
    violations.push({
      reasonCode: 'derived_metric_declared_as_driver',
      driverIds: collinearDriverIds,
      canonicalDriverId: sourceDriverIds[0] ?? derivedMetric.metricKey,
      derivedMetricKey: derivedMetric.metricKey,
      derivation: derivedMetric.derivation,
    });
  }

  if (violations.length === 0) {
    return { status: 'independent', violations: [] };
  }
  return { status: 'collinear', violations };
}

export function parseMultiDriverScenarioPlaybook(
  input: unknown,
): MultiDriverScenarioPlaybook {
  const record = requireRecord(input, 'MultiDriverScenarioPlaybook');
  requireString(record.playbookId, 'playbookId');
  requireString(record.version, 'version');
  requireString(record.formulaId, 'formulaId');
  requireString(record.formulaVersion, 'formulaVersion');

  const driverValues = requireArray(record.drivers, 'drivers');
  if (driverValues.length < 2 || driverValues.length > 4) {
    throw new Error('MultiDriverScenarioPlaybook must declare between 2 and 4 drivers.');
  }
  const drivers = driverValues.map((driver, index) =>
    parseDriverDefinition(driver, `drivers[${index}]`),
  );
  assertUniqueStrings(
    drivers.map((driver) => driver.driverId),
    'MultiDriverScenarioPlaybook driverIds',
  );

  const derivedMetricValues = requireArray(record.derivedMetrics, 'derivedMetrics');
  const declaredDriverIds = new Set(drivers.map((driver) => driver.driverId));
  const derivedMetrics = derivedMetricValues.map((metric, index) =>
    parseDerivedMetric(metric, `derivedMetrics[${index}]`, declaredDriverIds),
  );
  assertUniqueStrings(
    derivedMetrics.map((metric) => metric.metricKey),
    'MultiDriverScenarioPlaybook derived metric keys',
  );

  const caseValues = requireArray(record.cases, 'cases');
  if (caseValues.length !== SCENARIO_CASE_IDS.length) {
    throw new Error('MultiDriverScenarioPlaybook must declare exactly 3 cases.');
  }
  const driverIds = drivers.map((driver) => driver.driverId);
  const cases = caseValues.map((caseValue, index) =>
    parseCasePoint(caseValue, `cases[${index}]`, driverIds),
  );
  assertExactNamedCases(cases);

  const recommendedCount = cases.filter((casePoint) => casePoint.recommended).length;
  if (recommendedCount !== 1) {
    throw new Error('MultiDriverScenarioPlaybook must declare exactly one recommended case.');
  }

  assertAtMostOneGovernedFractionDriver(cases);
  assertIndependentDrivers(drivers, derivedMetrics);

  return input as MultiDriverScenarioPlaybook;
}

/**
 * Adds the emitter-owned facts that cannot be inferred from the core playbook
 * shape: which displayed metrics are deterministic outcomes and which numeric
 * binding keys are owned by cases versus the aggregate envelope.
 */
export function assertMultiDriverScenarioPlaybookContract(
  playbookInput: unknown,
  context: MultiDriverScenarioPlaybookContractContext,
): MultiDriverScenarioPlaybook {
  const playbook = parseMultiDriverScenarioPlaybook(playbookInput);
  const declaredDriverIds = playbook.drivers.map((driver) => driver.driverId);
  const derivedMetricRegistry = parseScenarioDerivedMetricRegistry(
    context.derivedMetricRegistry,
    declaredDriverIds,
  );
  assertIndependentDrivers(playbook.drivers, derivedMetricRegistry);

  const registryByMetricKey = new Map(
    derivedMetricRegistry.map((metric) => [metric.metricKey, metric]),
  );
  for (const declaredMetric of playbook.derivedMetrics) {
    const registeredMetric = registryByMetricKey.get(declaredMetric.metricKey);
    if (registeredMetric === undefined) {
      throw new Error(
        `Playbook derived metric ${declaredMetric.metricKey} must exist in the `
          + 'authoritative derived-metric registry.',
      );
    }
    if (
      !haveSameStringMembers(
        declaredMetric.derivedFromDriverIds,
        registeredMetric.derivedFromDriverIds,
      )
      || declaredMetric.derivation !== registeredMetric.derivation
    ) {
      throw new Error(
        `Playbook derived metric ${declaredMetric.metricKey} must match its `
          + 'authoritative registry declaration.',
      );
    }
  }

  const derivedMetricKeys = new Set(
    playbook.derivedMetrics.map((metric) => metric.metricKey),
  );
  const derivedCardMetricKeys = validateStringArray(
    context.derivedCardMetricKeys,
    'context.derivedCardMetricKeys',
  );
  assertUniqueStrings(derivedCardMetricKeys, 'context.derivedCardMetricKeys');
  for (const metricKey of derivedCardMetricKeys) {
    if (!derivedMetricKeys.has(metricKey) || !registryByMetricKey.has(metricKey)) {
      throw new Error(
        `Deterministic card metric ${metricKey} must be declared in both the `
          + 'playbook and authoritative derived-metric registry.',
      );
    }
  }

  const caseBindings = requireRecord(
    context.caseNumericBindingKeys,
    'context.caseNumericBindingKeys',
  );
  assertExactKeys(
    caseBindings,
    SCENARIO_CASE_IDS,
    'context.caseNumericBindingKeys must define exactly conservative, base, and stretch.',
  );
  const caseBindingKeys = SCENARIO_CASE_IDS.map((caseId) =>
    requireString(caseBindings[caseId], `context.caseNumericBindingKeys.${caseId}`),
  );
  assertUniqueStrings(caseBindingKeys, 'context.caseNumericBindingKeys');

  const aggregateBindingKeys = validateStringArray(
    context.aggregateNumericBindingKeys,
    'context.aggregateNumericBindingKeys',
  );
  assertUniqueStrings(aggregateBindingKeys, 'context.aggregateNumericBindingKeys');
  const aggregateBindingSet = new Set(aggregateBindingKeys);
  const collisions = caseBindingKeys.filter((key) => aggregateBindingSet.has(key));
  if (collisions.length > 0) {
    throw new Error(
      'MultiDriverScenarioPlaybook case numeric binding keys must be disjoint from '
        + `aggregate binding keys; collisions: ${collisions.join(', ')}.`,
    );
  }

  return playbook;
}

export function parseScenarioDerivedMetricRegistry(
  input: unknown,
  declaredDriverIds: readonly string[],
): readonly ScenarioDerivedMetricRegistryEntry[] {
  const validatedDriverIds = validateStringArray(
    declaredDriverIds,
    'declaredDriverIds',
  );
  assertUniqueStrings(validatedDriverIds, 'declaredDriverIds');
  const driverIdSet = new Set(validatedDriverIds);
  const entries = requireArray(input, 'derivedMetricRegistry').map((value, index) => {
    const path = `derivedMetricRegistry[${index}]`;
    const declaration = parseDerivedMetric(value, path, driverIdSet);
    const record = requireRecord(value, path);
    const renderAs = requireString(record.renderAs, `${path}.renderAs`);
    if (renderAs !== 'outcome') {
      throw new Error(`${path}.renderAs must be outcome.`);
    }
    return {
      ...declaration,
      renderAs,
    } satisfies ScenarioDerivedMetricRegistryEntry;
  });
  assertUniqueStrings(
    entries.map((entry) => entry.metricKey),
    'derivedMetricRegistry metric keys',
  );
  return entries;
}

export function parseScenarioDriverFormulaOutput(
  input: unknown,
  expectedDriverIds?: readonly string[],
): ScenarioDriverFormulaOutput {
  const record = requireRecord(input, 'ScenarioDriverFormulaOutput');
  const caseValueUsd = requireFiniteNumber(record.caseValueUsd, 'caseValueUsd');
  const baselineValueUsd = requireFiniteNumber(
    record.baselineValueUsd,
    'baselineValueUsd',
  );
  requireFiniteNumber(record.baselineVarianceUsd, 'baselineVarianceUsd', {
    min: 0,
    max: 0.01,
  });
  const driverEcho = parseNumericRecord(record.driverEcho, 'driverEcho');
  const axisContributions = parseNumericRecord(
    record.axisContributionsUsd,
    'axisContributionsUsd',
  );

  const driverEchoIds = Object.keys(driverEcho);
  if (driverEchoIds.length < 2 || driverEchoIds.length > 4) {
    throw new Error('ScenarioDriverFormulaOutput driverEcho must contain 2 to 4 drivers.');
  }
  assertExactKeys(
    axisContributions,
    driverEchoIds,
    'ScenarioDriverFormulaOutput axisContributionsUsd must match driverEcho exactly.',
  );
  if (expectedDriverIds !== undefined) {
    const validatedExpectedIds = validateStringArray(
      expectedDriverIds,
      'expectedDriverIds',
    );
    assertUniqueStrings(validatedExpectedIds, 'expectedDriverIds');
    assertExactKeys(
      driverEcho,
      validatedExpectedIds,
      'ScenarioDriverFormulaOutput driverEcho must match the declared driverIds exactly.',
    );
  }

  const interactionResidualUsd = requireFiniteNumber(
    record.interactionResidualUsd,
    'interactionResidualUsd',
  );
  const caseDeltaUsd = caseValueUsd - baselineValueUsd;
  const decomposedDeltaUsd =
    Object.values(axisContributions).reduce((sum, contribution) => sum + contribution, 0)
    + interactionResidualUsd;
  if (caseDeltaUsd !== decomposedDeltaUsd) {
    throw new Error(
      'ScenarioDriverFormulaOutput caseDeltaUsd must equal the sum of '
        + 'axisContributionsUsd plus interactionResidualUsd exactly.',
    );
  }

  return input as ScenarioDriverFormulaOutput;
}

export function parseScenarioFeasibleMaxCornerEvaluation(
  input: unknown,
  drivers: readonly ScenarioDriverDefinition[],
): ScenarioFeasibleMaxCornerEvaluation {
  const record = requireRecord(input, 'ScenarioFeasibleMaxCornerEvaluation');
  const validatedDrivers = parseExpectedDriverDefinitions(drivers, 'drivers');
  validateFeasibleMaxCorner(
    record,
    'ScenarioFeasibleMaxCornerEvaluation',
    validatedDrivers,
  );
  return input as ScenarioFeasibleMaxCornerEvaluation;
}

export function parseScenarioTargetVerdict(
  input: unknown,
  drivers: readonly ScenarioDriverDefinition[],
): ScenarioTargetVerdict {
  const record = requireRecord(input, 'ScenarioTargetVerdict');
  const validatedDrivers = parseExpectedDriverDefinitions(drivers, 'drivers');
  const status = requireEnum(record.status, ['reachable', 'nonexecutable'], 'status');
  const requestedTargetUsd = requireFiniteNumber(
    record.requestedTargetUsd,
    'requestedTargetUsd',
    { min: 0 },
  );

  if (status === 'reachable') {
    parseCasePoint(
      record.casePoint,
      'casePoint',
      validatedDrivers.map((driver) => driver.driverId),
    );
    return input as ScenarioReachableTargetVerdict;
  }

  const reasonCode = requireString(record.reasonCode, 'reasonCode');
  if (reasonCode !== 'target_exceeds_reachable_driver_space') {
    throw new Error(
      'ScenarioTargetVerdict.reasonCode must be '
        + 'target_exceeds_reachable_driver_space.',
    );
  }
  const evaluation = validateFeasibleMaxCorner(
    record,
    'ScenarioTargetVerdict',
    validatedDrivers,
  );
  const maxReachableUsd = evaluation.maxReachableUsd;
  if (requestedTargetUsd <= maxReachableUsd) {
    throw new Error(
      'Nonexecutable ScenarioTargetVerdict requestedTargetUsd must be greater than '
        + 'maxReachableUsd.',
    );
  }
  requireString(record.narrative, 'narrative');
  return input as ScenarioNonexecutableTargetVerdict;
}

function parseDriverDefinition(value: unknown, path: string): ScenarioDriverDefinition {
  const record = requireRecord(value, path);
  const driverId = requireString(record.driverId, `${path}.driverId`);
  if (!/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/.test(driverId)) {
    throw new Error(`${path}.driverId must be a snake_case fixed key.`);
  }
  requireString(record.label, `${path}.label`);
  requireEnum(record.unit, SCENARIO_DRIVER_UNITS, `${path}.unit`);
  parseDriverBinding(record.binding, `${path}.binding`);

  const domain = requireRecord(record.domain, `${path}.domain`);
  const min = parseDomainBound(domain.min, `${path}.domain.min`);
  const max = parseDomainBound(domain.max, `${path}.domain.max`);
  if (min.value >= max.value) {
    throw new Error(`${path}.domain.min.value must be strictly less than max.value.`);
  }
  requireString(record.narrationTemplate, `${path}.narrationTemplate`);
  return value as ScenarioDriverDefinition;
}

function parseDriverBinding(value: unknown, path: string): ScenarioDriverBinding {
  const record = requireRecord(value, path);
  const kind = requireEnum(
    record.kind,
    ['governed_signal', 'governed_rows', 'labeled_benchmark'],
    `${path}.kind`,
  );
  switch (kind) {
    case 'governed_signal':
      requireString(record.stateKey, `${path}.stateKey`);
      break;
    case 'governed_rows':
      requireString(record.rowPath, `${path}.rowPath`);
      requireEnum(
        record.aggregation,
        SCENARIO_DRIVER_ROW_AGGREGATIONS,
        `${path}.aggregation`,
      );
      break;
    case 'labeled_benchmark':
      requireString(record.referencePackContextKind, `${path}.referencePackContextKind`);
      requireString(record.metricId, `${path}.metricId`);
      requireString(record.derivation, `${path}.derivation`);
      break;
  }
  return value as ScenarioDriverBinding;
}

function parseDomainBound(value: unknown, path: string): ScenarioDriverDomainBound {
  const record = requireRecord(value, path);
  requireFiniteNumber(record.value, `${path}.value`);
  requireString(record.rationale, `${path}.rationale`);
  requireString(record.sourceRef, `${path}.sourceRef`);
  return value as ScenarioDriverDomainBound;
}

function parseDerivedMetric(
  value: unknown,
  path: string,
  declaredDriverIds: ReadonlySet<string>,
): ScenarioDerivedMetricDeclaration {
  const record = requireRecord(value, path);
  requireString(record.metricKey, `${path}.metricKey`);
  const derivedFromDriverIds = validateStringArray(
    record.derivedFromDriverIds,
    `${path}.derivedFromDriverIds`,
  );
  if (derivedFromDriverIds.length === 0) {
    throw new Error(`${path}.derivedFromDriverIds must not be empty.`);
  }
  assertUniqueStrings(derivedFromDriverIds, `${path}.derivedFromDriverIds`);
  for (const driverId of derivedFromDriverIds) {
    if (!declaredDriverIds.has(driverId)) {
      throw new Error(
        `${path}.derivedFromDriverIds references unknown driver ${driverId}.`,
      );
    }
  }
  requireString(record.derivation, `${path}.derivation`);
  return value as ScenarioDerivedMetricDeclaration;
}

function parseCasePoint(
  value: unknown,
  path: string,
  driverIds: readonly string[],
): ScenarioCasePoint {
  const casePoint = parseStandaloneCasePoint(value, path);
  const driverSettings = requireRecord(
    casePoint.driverSettings,
    `${path}.driverSettings`,
  );
  assertExactKeys(
    driverSettings,
    driverIds,
    `${path}.driverSettings must set exactly every declared driver.`,
  );
  return casePoint;
}

function parseStandaloneCasePoint(value: unknown, path: string): ScenarioCasePoint {
  const record = requireRecord(value, path);
  requireEnum(record.caseId, SCENARIO_CASE_IDS, `${path}.caseId`);
  requireString(record.label, `${path}.label`);
  requireEnum(record.riskLevel, ['low', 'medium', 'high'], `${path}.riskLevel`);
  const driverSettings = requireRecord(record.driverSettings, `${path}.driverSettings`);
  const driverIds = Object.keys(driverSettings);
  if (driverIds.length === 0) {
    throw new Error(`${path}.driverSettings must not be empty.`);
  }
  for (const driverId of driverIds) {
    parseDriverSettingSelector(driverSettings[driverId], `${path}.driverSettings.${driverId}`);
  }
  requireString(record.objective, `${path}.objective`);
  requireString(record.recommendation, `${path}.recommendation`);
  requireBoolean(record.recommended, `${path}.recommended`);
  return value as ScenarioCasePoint;
}

function parseDriverSettingSelector(
  value: unknown,
  path: string,
): ScenarioDriverSettingSelector {
  const record = requireRecord(value, path);
  const kind = requireEnum(
    record.kind,
    [
      'observed_baseline',
      'portfolio_stat',
      'benchmark_band_edge',
      'strategy_target',
      'governed_fraction_of_baseline',
    ],
    `${path}.kind`,
  );
  switch (kind) {
    case 'portfolio_stat':
      requireEnum(record.stat, ['minimum', 'median', 'maximum'], `${path}.stat`);
      break;
    case 'benchmark_band_edge':
      requireEnum(record.edge, ['low', 'high'], `${path}.edge`);
      break;
    case 'governed_fraction_of_baseline':
      requireFiniteNumber(record.factorPct, `${path}.factorPct`, {
        min: 0,
        exclusiveMin: true,
      });
      break;
    case 'observed_baseline':
    case 'strategy_target':
      break;
  }
  return value as ScenarioDriverSettingSelector;
}

function parseTargetBindingConstraint(
  value: unknown,
  path: string,
): ScenarioTargetBindingConstraint {
  const record = requireRecord(value, path);
  requireString(record.driverId, `${path}.driverId`);
  requireFiniteNumber(record.limitValue, `${path}.limitValue`);
  requireString(record.limitRationale, `${path}.limitRationale`);
  requireString(record.sourceRef, `${path}.sourceRef`);
  requireFiniteNumber(record.axisHeadroomUsd, `${path}.axisHeadroomUsd`, { min: 0 });
  return value as ScenarioTargetBindingConstraint;
}

function validateFeasibleMaxCorner(
  record: Readonly<Record<string, unknown>>,
  path: string,
  drivers: readonly ScenarioDriverDefinition[],
): ScenarioFeasibleMaxCornerEvaluation {
  const maxReachableUsd = requireFiniteNumber(
    record.maxReachableUsd,
    `${path}.maxReachableUsd`,
    { min: 0 },
  );
  const constraintValues = requireArray(
    record.bindingConstraints,
    `${path}.bindingConstraints`,
  );
  if (constraintValues.length === 0) {
    throw new Error(`${path}.bindingConstraints must not be empty.`);
  }
  const bindingConstraints = constraintValues.map((constraint, index) =>
    parseTargetBindingConstraint(constraint, `${path}.bindingConstraints[${index}]`),
  );
  assertUniqueStrings(
    bindingConstraints.map((constraint) => constraint.driverId),
    `${path} binding constraint driverIds`,
  );
  const constraintByDriverId = Object.fromEntries(
    bindingConstraints.map((constraint) => [constraint.driverId, constraint]),
  );
  assertExactKeys(
    constraintByDriverId,
    drivers.map((driver) => driver.driverId),
    `${path}.bindingConstraints must define exactly every declared driver.`,
  );
  for (const driver of drivers) {
    const constraint = constraintByDriverId[driver.driverId];
    if (constraint.limitValue !== driver.domain.max.value) {
      throw new Error(
        `${path}.bindingConstraints limitValue for ${driver.driverId} must equal `
          + 'its governed domain.max.value.',
      );
    }
    if (constraint.limitRationale !== driver.domain.max.rationale) {
      throw new Error(
        `${path}.bindingConstraints limitRationale for ${driver.driverId} must equal `
          + 'its governed domain.max.rationale.',
      );
    }
    if (constraint.sourceRef !== driver.domain.max.sourceRef) {
      throw new Error(
        `${path}.bindingConstraints sourceRef for ${driver.driverId} must equal `
          + 'its governed domain.max.sourceRef.',
      );
    }
  }
  for (let index = 1; index < bindingConstraints.length; index += 1) {
    if (
      bindingConstraints[index - 1].axisHeadroomUsd
      < bindingConstraints[index].axisHeadroomUsd
    ) {
      throw new Error(
        `${path}.bindingConstraints must be ranked by descending axisHeadroomUsd.`,
      );
    }
  }
  return { maxReachableUsd, bindingConstraints };
}

function parseExpectedDriverDefinitions(
  value: unknown,
  path: string,
): readonly ScenarioDriverDefinition[] {
  const driverValues = requireArray(value, path);
  if (driverValues.length < 2 || driverValues.length > 4) {
    throw new Error(`${path} must contain between 2 and 4 driver definitions.`);
  }
  const drivers = driverValues.map((driver, index) =>
    parseDriverDefinition(driver, `${path}[${index}]`),
  );
  assertUniqueStrings(
    drivers.map((driver) => driver.driverId),
    `${path} driverIds`,
  );
  return drivers;
}

function assertExactNamedCases(cases: readonly ScenarioCasePoint[]): void {
  const actualIds = cases.map((casePoint) => casePoint.caseId);
  assertUniqueStrings(actualIds, 'MultiDriverScenarioPlaybook caseIds');
  const actualSet = new Set(actualIds);
  if (SCENARIO_CASE_IDS.some((caseId) => !actualSet.has(caseId))) {
    throw new Error(
      'MultiDriverScenarioPlaybook must declare exactly 3 cases: '
        + 'conservative, base, and stretch.',
    );
  }
}

function assertAtMostOneGovernedFractionDriver(
  cases: readonly ScenarioCasePoint[],
): void {
  const fractionDriverIds = new Set<string>();
  for (const casePoint of cases) {
    for (const [driverId, selector] of Object.entries(casePoint.driverSettings)) {
      if (selector.kind === 'governed_fraction_of_baseline') {
        fractionDriverIds.add(driverId);
      }
    }
  }
  if (fractionDriverIds.size > 1) {
    throw new Error(
      'MultiDriverScenarioPlaybook permits at most one driver to use '
        + 'governed_fraction_of_baseline.',
    );
  }
}

function assertIndependentDrivers(
  drivers: readonly ScenarioDriverDefinition[],
  derivedMetrics: readonly ScenarioDerivedMetricDeclaration[],
): void {
  const report = detectScenarioDriverCollinearity(drivers, derivedMetrics);
  if (report.status === 'independent') return;
  const violation = report.violations[0];
  if (violation.reasonCode === 'shared_binding_identity') {
    throw new Error(
      `Drivers ${violation.driverIds.join(', ')} share binding identity `
        + `${violation.bindingIdentity}; present them as one driver.`,
    );
  }
  throw new Error(
    `Driver ${violation.driverIds[violation.driverIds.length - 1]} is a deterministic outcome of `
      + `${violation.driverIds.slice(0, -1).join(', ')}; present it as a derived metric.`,
  );
}

function parseNumericRecord(value: unknown, path: string): Record<string, number> {
  const record = requireRecord(value, path);
  const numericRecord: Record<string, number> = {};
  for (const [key, item] of Object.entries(record)) {
    requireString(key, `${path} key`);
    numericRecord[key] = requireFiniteNumber(item, `${path}.${key}`);
  }
  return numericRecord;
}

function validateStringArray(value: unknown, path: string): string[] {
  return requireArray(value, path).map((item, index) =>
    requireString(item, `${path}[${index}]`),
  );
}

function assertUniqueStrings(values: readonly string[], path: string): void {
  const duplicates = values.filter((value, index) => values.indexOf(value) !== index);
  if (duplicates.length > 0) {
    throw new Error(`${path} must be unique; duplicates: ${[...new Set(duplicates)].join(', ')}.`);
  }
}

function assertExactKeys(
  record: Readonly<Record<string, unknown>>,
  expectedKeys: readonly string[],
  message: string,
): void {
  const actualKeys = Object.keys(record);
  const actualSet = new Set(actualKeys);
  const expectedSet = new Set(expectedKeys);
  const matches =
    actualSet.size === expectedSet.size
    && actualKeys.every((key) => expectedSet.has(key));
  if (!matches) throw new Error(message);
}

function haveSameStringMembers(
  left: readonly string[],
  right: readonly string[],
): boolean {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((value) => rightSet.has(value));
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${path} must be an array.`);
  }
  return value;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${path} is required.`);
  }
  return value;
}

function requireBoolean(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`${path} must be a boolean.`);
  }
  return value;
}

function requireFiniteNumber(
  value: unknown,
  path: string,
  options?: Readonly<{ min?: number; max?: number; exclusiveMin?: boolean }>,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${path} must be a finite number.`);
  }
  if (options?.min !== undefined) {
    const outsideMinimum = options.exclusiveMin
      ? value <= options.min
      : value < options.min;
    if (outsideMinimum) {
      const comparator = options.exclusiveMin ? 'greater than' : 'at least';
      throw new Error(`${path} must be ${comparator} ${options.min}.`);
    }
  }
  if (options?.max !== undefined && value > options.max) {
    throw new Error(`${path} must be at most ${options.max}.`);
  }
  return value;
}

function requireEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw new Error(`${path} must be one of: ${allowed.join(', ')}.`);
  }
  return value as T;
}

function safeParse<T>(parser: () => T): ScenarioContractParseResult<T> {
  try {
    return { success: true, data: parser() };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}
